/*
 * The plumbing of a module's slice of the client model, written once.
 *
 * Core keeps the vocabulary every module speaks and hands each module one opaque entry under
 * `model.modules[<id>]` (modules.md §8). Core never looks inside it, so every module needs the same
 * four things around its own type: the slice out of a model, which is one shared empty slice until
 * the module has written its first; the model with the slice replaced, keeping the identity of
 * everything else; and the slice read back off the disk on a warm boot. They are rules of the
 * module contract and not of one module, so they are defined here and a module's `slice.ts` keeps
 * only what is its own: the type, the empty value and what of it is worth a disk (`dehydrate`).
 *
 * The shared empty slice is what keeps a render that comes before the first write from seeing a new
 * object on every call: zustand compares a selector's answer with `Object.is`, and a fresh empty
 * slice per call re-renders for ever (REUSE C5).
 */

import type { Model } from "./model.ts"

export interface ModuleSlice<S extends object> {
	/** The module whose entry of `model.modules` this is. */
	readonly id: string
	/** This module's slice; the shared empty one until the module has written its first. */
	readonly of: (model: Model) => S
	/** The model with this module's slice replaced: a new model sharing everything else. */
	readonly with: (model: Model, next: S) => Model
	/**
	 * A slice read back off the disk, as a new visit must see it (`FrontendModule.hydrate`): a fresh
	 * empty slice with what the cache legitimately holds copied onto it, field by field.
	 *
	 * Core restores a stored slice verbatim, and the stored value is a cache of a shape that keeps
	 * moving. A field the module has added since the cache was written is missing from it and would
	 * reach a render as `undefined` where every reader expects a record; a field the module no longer
	 * has is dropped rather than carried; and a `perVisit` field — one that belongs to the visit, like
	 * chat's unread anchor, pinned again the moment the reader arrives — is never read from the cache
	 * at all. `MODEL_VERSION` is the backstop and not the answer, because bumping it throws away every
	 * reader's whole model for one field.
	 */
	readonly hydrate: (stored: unknown) => S
}

/**
 * The plumbing of one module's slice. `empty` builds a fresh empty slice per call; `perVisit` names
 * the fields `hydrate` leaves at their empty value whatever the cache holds.
 */
export function defineModuleSlice<S extends object>(
	id: string,
	empty: () => S,
	options: { perVisit?: readonly (keyof S & string)[] } = {},
): ModuleSlice<S> {
	const shared = empty()
	const perVisit: readonly string[] = options.perVisit ?? []
	return {
		id,
		of: (model) => (model.modules[id] as S | undefined) ?? shared,
		with: (model, next) => ({ ...model, modules: { ...model.modules, [id]: next } }),
		hydrate: (stored) => {
			const slice = empty()
			if (typeof stored !== "object" || stored === null) return slice
			// Against the empty slice's *own* fields: what the cache does not carry stays empty, and a
			// field this module no longer has — or never had — is dropped rather than carried.
			for (const [field, value] of Object.entries(stored as Record<string, unknown>)) {
				if (value === undefined || !Object.hasOwn(slice, field) || perVisit.includes(field))
					continue
				Object.assign(slice, { [field]: value })
			}
			return slice
		},
	}
}
